/**
 * Customer Address Delete Action
 * Triggered by: arc:action:customerAddressDelete
 */

export const runCustomerAddressDelete = async (data = {}) => {
    try {
        const { 
            addressId,
            confirmDelete = false,
            showConfirmation = true,
            customerId,
            form
        } = data;

        if (!addressId) {
            throw new Error('Address ID is required');
        }

        // Show confirmation dialog if required
        if (showConfirmation && !confirmDelete) {
            const confirmed = await showDeleteConfirmation(addressId);
            if (!confirmed) {
                return {
                    success: false,
                    cancelled: true,
                    message: 'Address deletion cancelled'
                };
            }
        }

        // Dispatch delete attempt event
        window.dispatchEvent(new CustomEvent('customer:addressDeleteAttempt', {
            detail: {
                addressId,
                customerId
            }
        }));

        // Prepare form data
        const formData = new FormData();
        formData.append('_method', 'delete');
        formData.append('utf8', '✓');

        // Submit delete request
        const response = await fetch(`/account/addresses/${addressId}`, {
            method: 'POST',
            body: formData,
            headers: {
                'Accept': 'application/json, text/html',
            }
        });

        if (response.ok) {
            // Check if response is JSON (error) or HTML (success redirect)
            const contentType = response.headers.get('content-type');
            
            if (contentType && contentType.includes('application/json')) {
                // JSON response usually means error
                const errorData = await response.json();
                throw new Error(errorData.message || 'Address deletion failed');
            } else {
                // HTML response - check for errors
                const responseText = await response.text();
                const parser = new DOMParser();
                const doc = parser.parseFromString(responseText, 'text/html');
                
                // Look for error messages
                const errorElements = doc.querySelectorAll('.errors li, .error, [data-error]');
                if (errorElements.length > 0) {
                    const errors = Array.from(errorElements).map(el => el.textContent.trim());
                    throw new Error(errors.join(', '));
                }
                
                // Success
                window.dispatchEvent(new CustomEvent('customer:addressDeleteSuccess', {
                    detail: {
                        addressId,
                        customerId,
                        timestamp: Date.now()
                    }
                }));

                // Remove address element from DOM if it exists
                removeAddressFromDOM(addressId);

                return {
                    success: true,
                    addressId,
                    message: 'Address deleted successfully'
                };
            }
        } else {
            // Handle HTTP errors
            let errorMessage = 'Address deletion failed';
            
            try {
                const errorText = await response.text();
                const parser = new DOMParser();
                const doc = parser.parseFromString(errorText, 'text/html');
                const errorElements = doc.querySelectorAll('.errors li, .error, [data-error]');
                
                if (errorElements.length > 0) {
                    const errors = Array.from(errorElements).map(el => el.textContent.trim());
                    errorMessage = errors.join(', ');
                }
            } catch (e) {
                console.error('Error parsing response:', e);
            }

            throw new Error(errorMessage);
        }

    } catch (error) {
        console.error('Customer Address Delete error:', error);
        
        window.dispatchEvent(new CustomEvent('customer:addressDeleteError', {
            detail: {
                error: error.message,
                addressId: data.addressId
            }
        }));

        return {
            success: false,
            error: error.message
        };
    }
};

// Show delete confirmation dialog
const showDeleteConfirmation = async (addressId) => {
    return new Promise((resolve) => {
        // Dispatch confirmation event - theme can handle this with a modal
        window.dispatchEvent(new CustomEvent('customer:addressDeleteConfirm', {
            detail: {
                addressId,
                onConfirm: () => resolve(true),
                onCancel: () => resolve(false)
            }
        }));

        // Fallback to browser confirm if no custom handler
        setTimeout(() => {
            const confirmed = confirm('Are you sure you want to delete this address?');
            resolve(confirmed);
        }, 100);
    });
};

// Remove address from DOM
const removeAddressFromDOM = (addressId) => {
    const addressElement = document.querySelector(`[data-address-id="${addressId}"]`);
    if (addressElement) {
        // Add fade out animation if supported
        if (addressElement.style) {
            addressElement.style.transition = 'opacity 0.3s ease';
            addressElement.style.opacity = '0';
            
            setTimeout(() => {
                addressElement.remove();
            }, 300);
        } else {
            addressElement.remove();
        }
    }
};

// Initialize address delete listeners
export const initializeCustomerAddressDelete = (config = {}) => {
    const {
        deleteButtonSelector = '[data-address-delete]',
        autoSetupButtons = true,
        showConfirmation = true
    } = config;

    if (autoSetupButtons) {
        setupAddressDeleteButtons(deleteButtonSelector, showConfirmation);
    }

    window.dispatchEvent(new CustomEvent('customerAddressDelete:initialized', {
        detail: { config, timestamp: Date.now() }
    }));
};

// Setup address delete button listeners
const setupAddressDeleteButtons = (buttonSelector, showConfirmation) => {
    const deleteButtons = document.querySelectorAll(buttonSelector);
    
    deleteButtons.forEach(button => {
        button.removeEventListener('click', handleAddressDeleteClick);
        button.addEventListener('click', handleAddressDeleteClick);
        
        // Store confirmation setting on button
        button.dataset.showConfirmation = showConfirmation;
    });
};

// Handle address delete button clicks
const handleAddressDeleteClick = async (event) => {
    event.preventDefault();
    
    const button = event.target;
    const addressId = button.dataset.addressId || button.closest('[data-address-id]')?.dataset.addressId;
    const customerId = button.dataset.customerId;
    const showConfirmation = button.dataset.showConfirmation !== 'false';

    if (!addressId) {
        console.error('Address ID not found for delete button');
        return;
    }

    // Disable button during deletion
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Deleting...';

    try {
        await window.dispatchEvent(new CustomEvent('arc:action:customerAddressDelete', {
            detail: {
                addressId,
                customerId,
                showConfirmation,
                confirmDelete: false
            }
        }));
    } finally {
        // Re-enable button (if still in DOM)
        if (button.isConnected) {
            button.disabled = false;
            button.textContent = originalText;
        }
    }
};

// Set default address
export const setDefaultAddress = async (addressId, customerId) => {
    try {
        const formData = new FormData();
        formData.append('_method', 'put');
        formData.append('utf8', '✓');
        formData.append('address[default]', '1');

        const response = await fetch(`/account/addresses/${addressId}`, {
            method: 'POST',
            body: formData,
            headers: {
                'Accept': 'application/json, text/html',
            }
        });

        if (response.ok) {
            window.dispatchEvent(new CustomEvent('customer:addressDefaultSet', {
                detail: {
                    addressId,
                    customerId,
                    timestamp: Date.now()
                }
            }));

            // Update DOM to reflect new default
            updateDefaultAddressInDOM(addressId);

            return {
                success: true,
                addressId,
                message: 'Default address updated'
            };
        } else {
            throw new Error('Failed to set default address');
        }
    } catch (error) {
        window.dispatchEvent(new CustomEvent('customer:addressDefaultError', {
            detail: {
                error: error.message,
                addressId
            }
        }));

        return {
            success: false,
            error: error.message
        };
    }
};

// Update default address in DOM
const updateDefaultAddressInDOM = (newDefaultAddressId) => {
    // Remove default indicators from all addresses
    const allDefaultIndicators = document.querySelectorAll('[data-address-default]');
    allDefaultIndicators.forEach(indicator => {
        indicator.style.display = 'none';
    });

    // Show default indicator for new default address
    const newDefaultIndicator = document.querySelector(`[data-address-id="${newDefaultAddressId}"] [data-address-default]`);
    if (newDefaultIndicator) {
        newDefaultIndicator.style.display = 'block';
    }
};

// Initialize default address buttons
export const initializeDefaultAddressButtons = (config = {}) => {
    const {
        defaultButtonSelector = '[data-address-set-default]',
        autoSetupButtons = true
    } = config;

    if (autoSetupButtons) {
        setupDefaultAddressButtons(defaultButtonSelector);
    }
};

// Setup default address button listeners
const setupDefaultAddressButtons = (buttonSelector) => {
    const defaultButtons = document.querySelectorAll(buttonSelector);
    
    defaultButtons.forEach(button => {
        button.removeEventListener('click', handleSetDefaultClick);
        button.addEventListener('click', handleSetDefaultClick);
    });
};

// Handle set default address clicks
const handleSetDefaultClick = async (event) => {
    event.preventDefault();
    
    const button = event.target;
    const addressId = button.dataset.addressId || button.closest('[data-address-id]')?.dataset.addressId;
    const customerId = button.dataset.customerId;

    if (!addressId) {
        console.error('Address ID not found for default button');
        return;
    }

    // Disable button during update
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Setting...';

    try {
        await setDefaultAddress(addressId, customerId);
    } finally {
        // Re-enable button
        button.disabled = false;
        button.textContent = originalText;
    }
};

export default runCustomerAddressDelete;
